import { useEffect, useLayoutEffect, useState } from 'react';
import { useLiveEventData } from '../hooks/useLiveEventData';
import { getWindowObject } from '@eventbrite/feature-detection';
import { generateRandomHast } from '../utils';
import { defineMessage, MessageDescriptor, FormattedMessage } from 'react-intl';
import { LoadingButton } from '@eventbrite/marmalade';
import { VERTICAL } from '@/types';
import styles from './CheckoutButton.module.scss';
import { TicketInformationResponse } from '@/pages/api/[eventId]/ticket-information.page';
import { recordComponentRenderTime } from '../../../../common/monitoring/rum-metrics';
import { LISTING_HEAP_IDS, DIRECT_AFF_CODE } from '@/components/_shared/constants';
import { useAppContext } from '@/components/_shared/context/AppContext';
import { QUERY_PARAMS } from '../../../../common/constants';
import { EBWidgetsScript } from '@/components/_shared/EBWidgetsScript/EBWidgetsScript';
import { getStableIdFromCookie } from '@/utils/widgetUtils';
import escape from 'lodash/escape';

const MESSAGES: Record<string, MessageDescriptor> = {
  RESERVE_SPOT: defineMessage({
    id: 'Reserve a spot',
    defaultMessage: 'Reserve a spot',
    description: 'Free Checkout button CTA',
  }),
  GET_TICKETS: defineMessage({
    id: 'Get tickets',
    defaultMessage: 'Get tickets',
    description: 'Checkout button CTA',
  }),
  REMIND_ME: defineMessage({
    id: 'Details',
    defaultMessage: 'Details',
    description: 'Remind me button CTA',
  }),
  SERIES: defineMessage({
    id: 'Check availability',
    defaultMessage: 'Check availability',
    description: 'Series button CTA',
  }),
  JOIN_WAITLIST: defineMessage({
    id: 'Join Waitlist',
    defaultMessage: 'Join Waitlist',
    description: 'Waitlist button CTA',
  }),
  RSVP_CONFIRM: defineMessage({
    id: 'Yes',
    defaultMessage: 'Yes',
    description: 'RSVP Yes button CTA',
  }),
};

const getCheckoutButtonLabel = (
  isRemindMe?: boolean,
  isSeries?: boolean,
  ticketInfo?: TicketInformationResponse | null,
  vertical?: string,
  isRSVP?: boolean,
) => {
  if (isRemindMe) {
    return MESSAGES.REMIND_ME;
  }
  if (isRSVP) {
    return MESSAGES.RSVP_CONFIRM;
  }
  if (isSeries) {
    return MESSAGES.SERIES;
  }
  if (ticketInfo?.availability?.waitlistAvailable || ticketInfo?.availability?.waitlistEnabled) {
    return MESSAGES.JOIN_WAITLIST;
  }
  return ticketInfo?.isFree || vertical === VERTICAL.REGISTRATION ? MESSAGES.RESERVE_SPOT : MESSAGES.GET_TICKETS;
};

export const CheckoutButton: React.FC<{ isRemindMe?: boolean; isRSVP?: boolean }> = ({
  isRemindMe = false,
  isRSVP = false,
}) => {
  const {
    data: { serverUrl, stage },
  } = useAppContext();
  const { ticketInfo, loading, eventId, handleGetTicketsClick, isSeries, vertical, seriesId } = useLiveEventData();

  const [modalTriggerElementId, setModalTriggerElementId] = useState<string | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    setModalTriggerElementId(`eventbrite-widget-modal-trigger-${eventId}-${generateRandomHast()}`);
  }, [eventId]);

  useLayoutEffect(() => {
    if (!modalTriggerElementId || !isScriptLoaded || loading) return;

    requestAnimationFrame(() => {
      const buttonElement = document.getElementById(modalTriggerElementId);
      const ebWidgets = getWindowObject('EBWidgets');

      if (!buttonElement || !ebWidgets) {
        return;
      }

      const searchParams = new URLSearchParams(getWindowObject('location')?.search ?? '');
      const affiliateCode = searchParams.get(QUERY_PARAMS.AFFILIATE_CODE) ?? DIRECT_AFF_CODE;
      const waitlistToken = searchParams.get(QUERY_PARAMS.WAITLIST_TOKEN);
      const rsvpToken = searchParams.get(QUERY_PARAMS.MIPA);
      const stableId = getStableIdFromCookie();
      const promoCode = ticketInfo?.appliedPromoCode || ticketInfo?.discounts?.[0]?.code;

      ebWidgets.createWidget({
        widgetType: 'checkout',
        eventId: waitlistToken ? eventId : isSeries ? seriesId : eventId,
        modal: true,
        modalTriggerElementId,
        affiliateCode,
        ...(promoCode ? { promoCode: escape(promoCode) } : {}),
        ...(waitlistToken ? { waitlistToken } : {}),
        ...(stableId ? { extraParams: [{ name: 'stableID', value: stableId }] } : {}),
        ...(rsvpToken ? { rsvpToken } : {}),
      });

      // Auto-open checkout modal when #tickets hash or waitlist token is present
      const hasTicketsHash = getWindowObject('location')?.hash === '#tickets';
      if (hasTicketsHash || waitlistToken) {
        buttonElement.click();
      }
    });
  }, [eventId, modalTriggerElementId, isScriptLoaded, loading, ticketInfo, seriesId, isSeries]);

  // Track button render time for monitoring
  useEffect(() => {
    if (modalTriggerElementId && !loading) {
      // Button is fully rendered and ready - record the timing
      recordComponentRenderTime('CheckoutButton');
    }
  }, [modalTriggerElementId, loading]);

  const buttonLabel = getCheckoutButtonLabel(isRemindMe, isSeries ?? false, ticketInfo, vertical, isRSVP);

  if (!modalTriggerElementId) {
    return null;
  }

  return (
    <>
      <EBWidgetsScript hostname={serverUrl} onScriptLoad={() => setIsScriptLoaded(true)} stage={stage} />
      <LoadingButton
        isLoading={loading}
        id={modalTriggerElementId}
        role="button"
        variant={isRemindMe ? 'secondary' : 'primary'}
        className={styles.button}
        data-testid="conversion-bar-checkout-button"
        data-heap-id={LISTING_HEAP_IDS.CHECKOUT_BUTTON}
        onClick={() => handleGetTicketsClick(buttonLabel.id)}
      >
        <FormattedMessage {...buttonLabel} />
      </LoadingButton>
    </>
  );
};
